"use client";

import Image from "next/image";
import { useEffect, useState } from "react";

// The overlay stays up at least this long so the staged progress reads as a
// search and not a flicker; the wizard holds the redirect until it has elapsed.
const MATCHING_MIN_MS = 2800;
const COMPLETE_HOLD_MS = 450;
const TICK_MS = 80;
// Progress creeps towards this while the request is in flight and only hits
// 100 when the wizard says the match landed.
const PENDING_CEILING = 0.92;

const PRIVATE_STAGES = [
  "Gennemgår alle vores godkendte firmaer",
  "Tjekker CVR, anmeldelser og kapacitet",
  "Holder dine ønsker op mod hvert firma",
  "Finder dit bedste match",
];
const BUSINESS_STAGES = [
  "Gennemgår alle vores godkendte firmaer",
  "Tjekker CVR, anmeldelser og kapacitet",
  "Holder jeres krav op mod hvert firma",
  "Finder jeres bedste match",
];
const STAGE_ICONS = [
  "/images/lineboil/16-danmarkskort-mini.webp",
  "/images/lineboil/07-formular-mini.webp",
  "/images/lineboil/08-tre-personer-mini.webp",
  "/images/lineboil/08-tre-personer-mini.webp",
];
const COMPLETE_ICON = "/images/lineboil/15-checkmark-mini.webp";
const BOIL_SEEDS = [1, 2, 3];

// Resolves once the overlay has been visible for MATCHING_MIN_MS, then holds
// briefly at 100 % so the customer sees the search finish.
export async function holdMatchingOverlay(startedAt: number, markComplete: () => void) {
  const remaining = MATCHING_MIN_MS - (Date.now() - startedAt);
  if (remaining > 0) await new Promise((r) => setTimeout(r, remaining));
  markComplete();
  await new Promise((r) => setTimeout(r, COMPLETE_HOLD_MS));
}

export function MatchingOverlay({
  isBusiness,
  complete,
}: {
  isBusiness: boolean;
  complete: boolean;
}) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const started = Date.now();
    const id = setInterval(() => setElapsed(Date.now() - started), TICK_MS);
    return () => clearInterval(id);
  }, []);

  const stages = isBusiness ? BUSINESS_STAGES : PRIVATE_STAGES;
  // Fast at first, then slowing: the eased curve reads as narrowing in.
  const pending = PENDING_CEILING * (1 - Math.exp(-elapsed / 1100));
  const pct = complete ? 100 : Math.round(pending * 100);
  const stageIndex = complete
    ? stages.length - 1
    : Math.min(stages.length - 1, Math.floor(elapsed / (MATCHING_MIN_MS / stages.length)));

  const activeIcon = complete ? COMPLETE_ICON : STAGE_ICONS[stageIndex];

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-0 z-50 flex items-center justify-center bg-white/95 px-6"
    >
      <svg aria-hidden="true" className="absolute size-0">
        {BOIL_SEEDS.map((seed) => (
          <filter key={seed} id={`line-boil-${seed}`}>
            <feTurbulence type="fractalNoise" baseFrequency="0.03" numOctaves="2" seed={seed} />
            <feDisplacementMap in="SourceGraphic" scale="3" />
          </filter>
        ))}
      </svg>
      <div className="w-full max-w-sm">
        <div className="relative size-24 animate-line-boil">
          {[...new Set([...STAGE_ICONS, COMPLETE_ICON])].map((src) => (
            <Image
              key={src}
              src={src}
              alt=""
              width={96}
              height={96}
              priority
              className={`absolute inset-0 transition-opacity duration-300 motion-reduce:transition-none ${
                src === activeIcon ? "opacity-100" : "opacity-0"
              }`}
            />
          ))}
        </div>
        <p className="mt-6 font-serif text-2xl font-bold tracking-tight text-ink">
          {complete
            ? isBusiness
              ? "Vi har fundet et firma til jer"
              : "Vi har fundet et firma til dig"
            : isBusiness
              ? "Finder et firma til jer"
              : "Finder et firma til dig"}
        </p>
        <div className="mt-5 h-1.5 w-full overflow-hidden rounded bg-border">
          <div
            className="h-full rounded bg-theme transition-[width] duration-200 ease-out"
            style={{ width: `${pct}%` }}
          />
        </div>
        <ul className="mt-4 space-y-1.5 text-sm leading-relaxed">
          {stages.map((stage, i) => {
            const done = complete || i < stageIndex;
            const current = !complete && i === stageIndex;
            return (
              <li
                key={stage}
                className={`flex gap-2 transition-colors duration-300 ${
                  done ? "text-ink-muted" : current ? "text-ink" : "text-ink-subtle/60"
                }`}
              >
                <span aria-hidden className={`w-4 shrink-0 ${done ? "text-theme" : ""}`}>
                  {done ? "✓" : current ? "→" : ""}
                </span>
                {stage}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
